import React from 'react';
import {
  Box, Button, IconButton, TextField, InputAdornment, Tooltip,
  Menu, MenuItem, ListItemIcon, ListItemText, Divider, alpha, useTheme,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import GridViewIcon from '@mui/icons-material/GridView';
import ViewListIcon from '@mui/icons-material/ViewList';
import RefreshIcon from '@mui/icons-material/Refresh';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DriveFolderUploadIcon from '@mui/icons-material/DriveFolderUpload';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import DeleteIcon from '@mui/icons-material/Delete';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';
import SortIcon from '@mui/icons-material/Sort';
import CheckIcon from '@mui/icons-material/Check';

const SORT_OPTIONS = [
  { value: 'name-asc',      label: 'Name A → Z' },
  { value: 'name-desc',     label: 'Name Z → A' },
  { value: 'modified-desc', label: 'Newest first' },
  { value: 'modified-asc',  label: 'Oldest first' },
  { value: 'size-desc',     label: 'Largest first' },
  { value: 'size-asc',      label: 'Smallest first' },
  { value: 'type',          label: 'By type' },
];

/**
 * A small toggle group that doesn't look disabled when inactive.
 * MUI ToggleButton renders grey-on-grey at rest which reads as "off",
 * so we draw our own to give the selected state real contrast.
 */
function ViewToggle({ value, onChange }) {
  const theme = useTheme();
  const cell = (mode, Icon, title) => {
    const active = value === mode;
    return (
      <Tooltip title={title}>
        <IconButton
          size="small"
          onClick={() => onChange(mode)}
          sx={{
            borderRadius: 1,
            color: active ? theme.palette.primary.contrastText : 'text.primary',
            bgcolor: active ? theme.palette.primary.main : 'transparent',
            '&:hover': {
              bgcolor: active ? theme.palette.primary.dark : alpha(theme.palette.primary.main, 0.12),
            },
            width: 32, height: 32,
          }}
        >
          <Icon fontSize="small" />
        </IconButton>
      </Tooltip>
    );
  };
  return (
    <Box sx={{
      display: 'inline-flex', gap: 0.25, p: 0.25,
      borderRadius: 1.5, bgcolor: alpha(theme.palette.text.primary, 0.05),
    }}>
      {cell('grid', GridViewIcon, 'Grid view')}
      {cell('list', ViewListIcon, 'List view')}
    </Box>
  );
}

// OPUS-1045: compact byte formatter for the folder-size info point.
const formatBytes = (n) => {
  if (!n || n < 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(Math.floor(Math.log(n) / Math.log(1024)), units.length - 1);
  return `${(n / Math.pow(1024, i)).toFixed(i ? 1 : 0)} ${units[i]}`;
};

export default function Toolbar({
  searchQuery, onSearchChange,
  viewMode, onViewModeChange,
  onRefresh,
  folderStats,
  onNewFolder,
  onUploadFiles,
  onUploadFolder,
  selectedCount,
  onClearSelection,
  onBulkDelete,
  onBulkMove,
  onBulkDownload,
  sort, onSortChange,
}) {
  const theme = useTheme();
  const [uploadAnchor, setUploadAnchor] = React.useState(null);
  const [sortAnchor, setSortAnchor] = React.useState(null);
  const fileInputRef = React.useRef(null);
  const folderInputRef = React.useRef(null);

  const hasSelection = selectedCount > 0;
  const activeSort = SORT_OPTIONS.find((o) => o.value === sort) || SORT_OPTIONS[0];

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', py: 1 }}>
      <TextField
        size="small"
        placeholder="Search this folder…"
        value={searchQuery}
        onChange={(e) => onSearchChange(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
            </InputAdornment>
          ),
          sx: {
            bgcolor: alpha(theme.palette.text.primary, 0.04),
            '& fieldset': { borderColor: 'transparent' },
            '&:hover fieldset': { borderColor: alpha(theme.palette.primary.main, 0.3) },
          },
        }}
        sx={{ minWidth: 280, flex: '0 1 360px' }}
      />

      <ViewToggle value={viewMode} onChange={onViewModeChange} />

      <Tooltip title="Sort by">
        <Button
          size="small"
          startIcon={<SortIcon />}
          endIcon={<ArrowDropDownIcon />}
          onClick={(e) => setSortAnchor(e.currentTarget)}
          sx={{
            color: 'text.primary',
            bgcolor: alpha(theme.palette.text.primary, 0.04),
            textTransform: 'none', fontWeight: 500, px: 1.25,
            '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.12) },
          }}
        >
          {activeSort.label}
        </Button>
      </Tooltip>
      <Menu anchorEl={sortAnchor} open={Boolean(sortAnchor)} onClose={() => setSortAnchor(null)}>
        {SORT_OPTIONS.map((opt) => (
          <MenuItem
            key={opt.value}
            selected={opt.value === sort}
            onClick={() => { onSortChange(opt.value); setSortAnchor(null); }}
          >
            <ListItemIcon>{opt.value === sort && <CheckIcon fontSize="small" />}</ListItemIcon>
            <ListItemText primary={opt.label} />
          </MenuItem>
        ))}
      </Menu>

      <Tooltip title="Refresh">
        <IconButton size="small" onClick={onRefresh}
          sx={{ color: 'text.primary', '&:hover': { color: theme.palette.primary.main } }}>
          <RefreshIcon />
        </IconButton>
      </Tooltip>

      <Box sx={{ flexGrow: 1 }} />

      {/* OPUS-1045: folder size / item-count information point. */}
      {folderStats && !hasSelection && (
        <Box sx={{ fontSize: 12.5, color: 'var(--text-dim)', fontWeight: 500, mr: 1, whiteSpace: 'nowrap' }}>
          {folderStats.count} {folderStats.count === 1 ? 'item' : 'items'} · {formatBytes(folderStats.bytes)}
        </Box>
      )}

      {hasSelection ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box sx={{
            px: 1.5, py: 0.5, borderRadius: 1,
            bgcolor: theme.palette.primary.main, color: theme.palette.primary.contrastText,
            fontSize: 13, fontWeight: 600, lineHeight: 1.6,
          }}>
            {selectedCount} selected
          </Box>
          <Tooltip title="Clear selection (Esc)">
            <IconButton size="small" onClick={onClearSelection}><CloseIcon /></IconButton>
          </Tooltip>
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
          <Button size="small" startIcon={<DownloadIcon />} onClick={onBulkDownload}
            sx={{ textTransform: 'none', color: 'text.primary' }}>
            Download
          </Button>
          <Button size="small" startIcon={<DriveFileMoveIcon />} onClick={onBulkMove}
            sx={{ textTransform: 'none', color: 'text.primary' }}>
            Move
          </Button>
          <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={onBulkDelete}
            variant="contained" disableElevation
            sx={{ textTransform: 'none' }}>
            Delete
          </Button>
        </Box>
      ) : (
        <>
          <Button
            size="small"
            startIcon={<CreateNewFolderIcon />}
            onClick={onNewFolder}
            sx={{
              textTransform: 'none', fontWeight: 500,
              color: 'text.primary',
              bgcolor: alpha(theme.palette.text.primary, 0.04),
              '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.12) },
            }}
          >
            New folder
          </Button>
          <Button
            size="small"
            variant="contained" disableElevation
            startIcon={<CloudUploadIcon />}
            endIcon={<ArrowDropDownIcon />}
            onClick={(e) => setUploadAnchor(e.currentTarget)}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Upload
          </Button>
          <Menu anchorEl={uploadAnchor} open={Boolean(uploadAnchor)} onClose={() => setUploadAnchor(null)}>
            <MenuItem onClick={() => { setUploadAnchor(null); fileInputRef.current?.click(); }}>
              <ListItemIcon><CloudUploadIcon fontSize="small" /></ListItemIcon>
              <ListItemText primary="Upload files" secondary="Or drop them anywhere" />
            </MenuItem>
            <MenuItem onClick={() => { setUploadAnchor(null); folderInputRef.current?.click(); }}>
              <ListItemIcon><DriveFolderUploadIcon fontSize="small" /></ListItemIcon>
              <ListItemText primary="Upload folder" secondary="Preserves the folder structure" />
            </MenuItem>
          </Menu>
          <input
            ref={fileInputRef} type="file" multiple hidden
            onChange={(e) => {
              const files = Array.from(e.target.files || []);
              if (files.length) onUploadFiles(files);
              e.target.value = '';
            }}
          />
          <input
            ref={folderInputRef} type="file" hidden
            webkitdirectory="" directory=""
            onChange={(e) => {
              const files = Array.from(e.target.files || []);
              if (files.length) onUploadFolder(files);
              e.target.value = '';
            }}
          />
        </>
      )}
    </Box>
  );
}
